import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { DicedHeroSection } from '../components/ui/diced-hero-section';
import '../styles/products-page-new.css';
import { getCategories, getProducts, getImageUrl } from '../lib/api';

const Products = ({ onCategoryClick, onProductClick }) => {
  const [categoriesWithProducts, setCategoriesWithProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'instant' });

    // Fetch categories and their products
    const fetchCategoriesWithProducts = async () => {
      try {
        const categories = await getCategories();

        // Fetch products for each category
        const categoriesWithProductsData = await Promise.all(
          categories.map(async (category) => {
            const products = await getProducts(category._id);
            return {
              ...category,
              products: products
            };
          })
        );

        // Filter out categories with no products
        const filteredCategories = categoriesWithProductsData.filter(
          cat => cat.products && cat.products.length > 0
        );

        setCategoriesWithProducts(filteredCategories);
        setLoading(false);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Failed to fetch categories and products:', err);
        }
        setLoading(false);
      }
    };

    fetchCategoriesWithProducts();
  }, []);

  // Helper function to get 4 product images (repeat if necessary)
  const getProductImages = (products) => {
    const images = [];
    const productList = products.slice(0, 4);

    // Fill the images array with 4 images
    for (let i = 0; i < 4; i++) {
      const product = productList[i % productList.length];
      images.push({
        title: product.name,
        image: getImageUrl(product.image, product.uploadedAt ? new Date(product.uploadedAt).getTime() : null)
      });
    }

    return images;
  };

  if (loading) {
    return (
      <div className="products-page-new">
        <Helmet>
          <title>Our Products</title>
          <meta name="description" content="Explore our comprehensive range of premium products including apparel, textiles, home décor, and accessories. Quality manufacturing and sustainable sourcing." />
          <link rel="canonical" href="https://sitcorpinternational.com/products" />
        </Helmet>
        <div className="loading-container">
          <div className="loading-spinner">Loading products...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page-new">
      <Helmet>
        <title>Our Products</title>
        <meta name="description" content="Explore our comprehensive range of premium products including apparel, textiles, home décor, and accessories. Quality manufacturing and sustainable sourcing." />
        <link rel="canonical" href="https://sitcorpinternational.com/products" />
        <meta property="og:title" content="Our Products - Sitcorp International" />
        <meta property="og:description" content="Explore our comprehensive range of premium products including apparel, textiles, home décor, and accessories." />
        <meta property="og:url" content="https://sitcorpinternational.com/products" />
      </Helmet>

      {categoriesWithProducts.length > 0 ? (
        categoriesWithProducts.map((category, index) => {
          const isBlackBackground = index % 2 === 1;
          const backgroundColor = isBlackBackground ? '#000000' : '#FFFFFF';
          const textColor = isBlackBackground ? '#FFFFFF' : '#000000';
          const secondaryTextColor = isBlackBackground ? '#E0E0E0' : '#404040';
          const separatorColor = isBlackBackground ? '#FFFFFF' : '#000000';
          const buttonBg = isBlackBackground ? '#FFFFFF' : '#000000';
          const buttonText = isBlackBackground ? '#000000' : '#FFFFFF';
          const buttonHoverBg = isBlackBackground ? '#E0E0E0' : '#333333';

          return (
            <DicedHeroSection
              key={category._id}
              topText={`Explore Our ${category.name}`}
              mainText={category.name}
              subMainText={category.description || `Discover our premium ${category.name.toLowerCase()} collection featuring the finest quality products designed for discerning customers.`}
              buttonText="View All"
              slides={getProductImages(category.products)}
              onMainButtonClick={() => onCategoryClick && onCategoryClick(category)}
              onGridImageClick={(index) => {
                const product = category.products[index % category.products.length];
                onProductClick && onProductClick(product);
              }}
              topTextStyle={{
                color: secondaryTextColor,
                fontSize: '1rem',
              }}
              mainTextStyle={{
                color: textColor,
                fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
                fontWeight: '700',
                marginBottom: '1rem',
              }}
              subMainTextStyle={{
                color: secondaryTextColor,
                fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                lineHeight: '1.6',
              }}
              buttonStyle={{
                backgroundColor: buttonBg,
                color: buttonText,
                borderRadius: '2rem',
                hoverColor: buttonHoverBg,
                hoverForeground: isBlackBackground ? '#000000' : '#FFFFFF',
              }}
              backgroundColor={backgroundColor}
              separatorColor={separatorColor}
              componentBorderRadius="0px"
              maxContentWidth="100%"
              mobileBreakpoint={1000}
            />
          );
        })
      ) : (
        <div className="no-products-container">
          <p>No products available at the moment.</p>
        </div>
      )}
    </div>
  );
};

export default Products;

